import React, { useState, ReactNode, ErrorInfo, useEffect } from 'react'

interface Props {
  children: ReactNode
}

const ErrorBoundary: React.FC<Props> = ({ children }) => {
  const [hasError, setHasError] = useState(false)

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    console.error('Uncaught error:', error, errorInfo)
    setHasError(true)
  }

  useEffect(() => {
    // "ResizeObserver loop completed with undelivered notifications" (and the older Chrome
    // wording, "...loop limit exceeded") is a benign browser warning, not an application error —
    // it fires whenever a ResizeObserver callback itself changes an observed element's size
    // (which MUI's autosizing multiline TextField does deliberately) and the browser can't
    // deliver every notification within one frame. It carries no stack and breaks nothing, but
    // this listener treated every `window` error as fatal and blanked the whole app for it.
    const isBenignResizeObserverNoise = (message: string) => message.startsWith('ResizeObserver loop')

    const errorListener = (errorEvent: ErrorEvent) => {
      if (isBenignResizeObserverNoise(errorEvent.message)) return
      handleError(new Error(errorEvent.message), { componentStack: errorEvent.filename })
    }

    window.addEventListener('error', errorListener)

    return () => {
      window.removeEventListener('error', errorListener)
    }
  }, [])

  if (hasError) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <h1>Something went wrong. Please reload</h1>
      </div>
    )
  }

  return <>{children}</>
}

export default ErrorBoundary
